import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { fetchProductsWithBuilds } from "src/ui/containers/dashboard/products/store";
import { makeStyles } from "tss-react/mui";

import { fetchAllGroups } from "@admin/api/group";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { ContentContainer } from "@dashboard/shared/styles";
import { EmteriaRoles } from "@data/roles";
import { ConfirmDialog } from "@devices/components/shared";
import { Box, DialogContentText, Paper, TableContainer, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { deleteProduct, fetchAllProducts } from "@products/api";
import TimeService from "@services/timeService/timeService";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { LoadingBox } from "@shared/Loading";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { fuzzyFilter } from "@shared/table/Tanstack.utils";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import BuildConfigDialog from "./components/BuildConfigDialog";
import { EditProductDialog } from "./components/EditProductDialog";
import { ActionCell, ActionCellProps } from "./components/TableCells";
import { VisibilityCell, VisibilityCellProps } from "./components/VisibilityCell";

const useStyles = makeStyles()((theme) => ({
    noDataBox: { display: "flex", justifyContent: "center", alignItems: "center", marginTop: theme.spacing(15), flex: 1 },
    card: { marginBottom: theme.spacing(5) },
    cardContent: { "&.MuiCardContent-root": { paddingTop: 0 } },
    cardTitleBox: { display: "flex", gap: theme.spacing(2), alignItems: "flex-end" },
    cell: { width: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    flex: { display: "flex", gap: theme.spacing(1) },
    tableContainer: { marginTop: theme.spacing(2) },
}));

type ProductTableData = {
    id: number;
    workspaceId: number;
    groupId: number | null;
    name: string;
    codename: string;
    creationDate: string;
    builds: number;
    androidVersion: string;
    visibility: VisibilityCellProps;
    actions: ActionCellProps;
};

const columnHelper = createColumnHelper<ProductTableData>();

const tableColumns = [
    columnHelper.accessor("id", {
        id: "id",
        enableSorting: true,
        header: "ID",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("workspaceId", {
        id: "workspaceId",
        enableSorting: true,
        header: () => (
            <Tooltip title={i18next.t("common:label.workspaceId")} placement="top">
                <span style={{ fontWeight: 600 }}>WID</span>
            </Tooltip>
        ),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("groupId", {
        id: "groupId",
        enableSorting: true,
        header: () => (
            <Tooltip title={i18next.t("common:label.groupId")} placement="top">
                <span style={{ fontWeight: 600 }}>GID</span>
            </Tooltip>
        ),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue() ?? "-"}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("name", {
        id: "name",
        header: i18next.t("common:label.name"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("codename", {
        id: "codename",
        header: i18next.t("common:label.codename"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("creationDate", {
        id: "creationDate",
        header: i18next.t("common:label.creationDate"),
        cell: (info) => (
            <MinimalTableCell>
                <Tooltip title={TimeService.getLocalDate(info.row.original.creationDate, "ISO", "dateTime")}>
                    <Typography>{TimeService.getLocalDate(info.row.original.creationDate, "ISO", "date")}</Typography>
                </Tooltip>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("androidVersion", {
        id: "androidVersion",
        header: i18next.t("common:label.androidVersion"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("builds", {
        id: "builds",
        header: i18next.t("common:label.builds"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("visibility", {
        id: "visibility",
        header: i18next.t("common:label.visibility"),
        cell: (info) => <VisibilityCell {...info.getValue()} />,
        enableSorting: false,
    }),
    columnHelper.accessor("actions", {
        id: "actions",
        header: "",
        cell: (info) => <ActionCell {...info.getValue()} />,
        enableSorting: false,
    }),
];

type State<T> = {
    data: T[];
    loading: boolean;
};
const DefaultState = {
    data: [],
    loading: false,
};

type DeleteState = {
    id: number | null;
    open: boolean;
    loading: boolean;
};
const DefaultDeleteState = {
    id: null,
    open: false,
    loading: false,
};

const AdminProductsPage = () => {
    const [query, setQuery] = React.useState("");
    const [products, setProducts] = React.useState<State<API.Product>>(DefaultState);
    const [deleteState, setDeleteState] = React.useState<DeleteState>(DefaultDeleteState);
    const [editProduct, setEditProduct] = React.useState<API.Product | null>(null);
    const [configProduct, setConfigProduct] = React.useState<API.Product | null>(null);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common", "pages"]);

    const table = useReactTable({
        data: products.data.map((item) => ({
            id: item.id,
            workspaceId: item.workspaceId,
            groupId: item.groupId,
            name: item.customBuildConfig?.productName ?? t("pages:adminProductsPage.unknownProductName"),
            codename: item.codename,
            creationDate: item.created,
            builds: item.numberOfBuilds,
            androidVersion: item.androidVersion,
            visibility: { product: item },
            actions: {
                product: item,
                isDeleting: deleteState.loading,
                onDelete: (id: number) => setDeleteState({ id, open: true, loading: false }),
                setConfigProduct,
                setEditProduct,
            },
        })),
        columns: tableColumns,
        filterFns: { fuzzy: fuzzyFilter },
        state: { globalFilter: query },
        defaultColumn: { size: 0 },
        autoResetPageIndex: false,
        initialState: {
            sorting: [{ id: "name", desc: false }],
            pagination: {
                pageIndex: 0,
                pageSize: 12,
            },
        },
        onGlobalFilterChange: setQuery,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });
    const tableRows = table.getPaginationRowModel().rows;

    const onDelete = async () => {
        if (deleteState.id && workspaceId !== undefined && workspaceId !== null) {
            try {
                setDeleteState((prev) => ({ ...prev, loading: true }));

                await deleteProduct(deleteState.id);

                showSuccessToast(t("pages:adminProductsPage.toast.productDeletedSuccess"));

                setProducts((prev) => ({
                    ...prev,
                    data: prev.data.filter((item) => item.id !== deleteState.id),
                }));
                await dispatch(fetchProductsWithBuilds({ workspaceId })).unwrap();
            } catch (error) {
                showErrorToast(error instanceof Error ? error.message : t("pages:adminProductsPage.toast.productDeletedError"));
            } finally {
                setDeleteState(DefaultDeleteState);
            }
        }
    };
    const onSearch = async (userEmail: string) => {
        if (!userEmail) {
            return;
        }

        try {
            await fetchAllGroups({ userEmail });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    const onClearSearch = () => {
        setQuery("");
    };

    const handleResetFilter = () => {
        setQuery("");
        table.setPageIndex(0);
    };

    React.useLayoutEffect(() => {
        setProducts((prev) => ({ ...prev, loading: true }));
        void fetchAllProducts()
            .then((data) => setProducts({ data, loading: false }))
            .catch((error) => {
                const err = error as Error;
                showErrorToast(err.message);
                setProducts({ data: [], loading: false });
            });
    }, []);

    if (products.loading) {
        return <LoadingBox />;
    }

    if (!products.data.length && !products.loading) {
        return (
            <Box className={classes.noDataBox}>
                <Typography>{t("pages:adminProductsPage.noProducts")}</Typography>
            </Box>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="Products"
                iconName="fa-solid fa-microchip"
                description={t("pages:adminProductsPage.description")}
            />
            <ContentContainer>
                <QueryBuilderField
                    placeholder={t("common:button.search")}
                    name="ownerEmail"
                    value={query}
                    onChange={setQuery}
                    onSubmit={onSearch}
                    onClear={onClearSearch}
                />
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Header key={header.id} {...header} />
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody
                            table={table}
                            filter={{ isActive: !!query.length, onResetFilter: handleResetFilter }}
                            noDataMessage={t("pages:adminProductsPage.noProducts")}
                        >
                            {tableRows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) =>
                                        flexRender(cell.column.columnDef.cell, {
                                            ...cell.getContext(),
                                            key: cell.id,
                                        }),
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </TableContainer>

                <CustomTablePagination
                    component="div"
                    rowsPerPageOptions={[12, 24, 48]}
                    count={table.getFilteredRowModel().rows.length}
                    rowsPerPage={table.getState().pagination.pageSize}
                    page={table.getState().pagination.pageIndex}
                    onPageChange={(event, newPage) => table.setPageIndex(newPage)}
                    onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
                />

                <ConfirmDialog
                    title={t("modals:adminDeleteProduct.title")}
                    dangerButton
                    primaryActionText={t("common:button.delete")}
                    open={deleteState.open}
                    onConfirm={onDelete}
                    onClose={() => setDeleteState((prev) => ({ ...prev, open: false }))}
                >
                    <DialogContentText>{t("modals:adminDeleteProduct.description")}</DialogContentText>
                </ConfirmDialog>
                {editProduct ? (
                    <EditProductDialog
                        product={editProduct}
                        onClose={() => setEditProduct(null)}
                        open={true}
                        title={t("modals:adminEditProduct.title")}
                        products={products.data}
                        setProducts={(items) => setProducts((prev) => ({ ...prev, data: items }))}
                    />
                ) : null}
                <BuildConfigDialog product={configProduct} open={!!configProduct} onClose={() => setConfigProduct(null)} />
            </ContentContainer>
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminProductsPage, {
        title: i18next.t("pages:adminProductsPage.metaTitle"),
        description: i18next.t("pages:adminProductsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaDeveloperRole],
);
